import { AsyncPipe, DatePipe } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';

import { parse, parseISO } from 'date-fns';
import isValid from 'date-fns/isValid';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith, switchMap, take, takeUntil, tap } from 'rxjs/operators';

import { MakeProvider } from '../../../../helpers/base-control-value-accessor';
import { SingleValueFormInput } from '../../../../helpers/single-value-form-input';
import { FormValue, FormValueState } from '../../../entities/form-value.entity';
import { DateError } from '../entities/date-errors.enum';
import { DateToEngineISOString } from '../helpers/engine-iso-string.helper';

type InputType = 'text' | 'date';
@Component({
    selector: 'sh-form-date-complex-base',
    templateUrl: './form-date-complex-base.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [MakeProvider(ShFormDateComplexBaseComponent)],
    standalone: true,
    imports: [ReactiveFormsModule, AsyncPipe]
})
export class ShFormDateComplexBaseComponent extends SingleValueFormInput<string | null> implements OnInit, OnDestroy {
    private readonly DATE_REGEXP: RegExp = new RegExp(/\d{4}-\d{2}-\d{2}/);

    private readonly indeterminate$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private readonly focus$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private readonly keyboardChange$: Subject<void> = new Subject<void>();

    private externalLocalTime?: string | null;

    @Input() min?: string;
    @Input() max?: string;
    @Input() autocomplete?: string;
    @Input() placeholder = '';
    @Input() id!: string;

    @Input() invalid?: boolean = false;
    @Input() required = false;
    @Input() describeLabelId = '';
    @Input() hasActionItems = false;

    @Input() indeterminateMsg = '';
    @Output() validationChange: EventEmitter<string> = new EventEmitter();

    @ViewChild('elementRef', { static: true }) elementRef!: ElementRef;

    public control: UntypedFormControl;

    public inputType$!: Observable<'text' | 'date'>;
    public message$!: Observable<string>;

    constructor(
        changeDetectorRef: ChangeDetectorRef,
        protected datePipe: DatePipe
    ) {
        super(changeDetectorRef);

        this.control = new UntypedFormControl();

        this.initPlaceholderMessage();
    }

    ngOnInit(): void {
        this.initInputTypeStream();
        this.initListeners();

        this.unsetControlAfterInvalidKeyboardInput();
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();

        this.indeterminate$.complete();
        this.focus$.complete();
        this.keyboardChange$.complete();
    }

    keyboardChange(_: KeyboardEvent): void {
        this.keyboardChange$.next();
    }

    focusChange(focusEvent: FocusEvent): void {
        if (focusEvent.type === 'focus') {
            this.focus$.next(true);
        } else if (focusEvent.type === 'blur') {
            this.focus$.next(false);
        }
    }

    set(value: any | null): void {
        // MED-5617: For consistency with metadata we always assume a date string will be provided
        const dateValue = value ? parseISO(value) : value;
        if (dateValue instanceof Date && !Number.isNaN(dateValue.getTime())) {
            const converted: string | null = this.datePipe.transform(dateValue, 'yyyy-MM-dd');
            this.externalLocalTime = this.datePipe.transform(dateValue, 'HH:mm:ss.SSS');
            this.indeterminate$.next(false);
            this.control.setValue(converted);
        } else {
            console.error('ShFormDateComplexBaseComponent:: FormValue does not contain a Date');
            this.unset();
        }
    }

    unset(): void {
        this.indeterminate$.next(false);
        this.control.setValue(null);
    }

    makeIndeterminate(): void {
        this.indeterminate$.next(true);
        this.control.setValue(null, { emitEvent: false });
    }

    private unsetControlAfterInvalidKeyboardInput(): void {
        const blur$: Observable<boolean> = this.focus$.pipe(filter(focus => !focus));

        this.keyboardChange$
            .pipe(
                switchMap(() => blur$),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                if (this.control.value === null) {
                    this.unset();
                }
            });
    }

    private initPlaceholderMessage(): void {
        this.message$ = this.indeterminate$.pipe(
            map(isIndeterminate => (isIndeterminate ? this.indeterminateMsg : this.placeholder))
        );
    }

    private initInputTypeStream(): void {
        this.inputType$ = combineLatest([this.indeterminate$, this.focus$]).pipe(
            map(([indeterminate, focus]) => (indeterminate && !focus ? 'text' : 'date')),
            distinctUntilChanged(),
            tap(this.refocusManually.bind(this)),
            startWith('date' as InputType)
        );
    }

    private refocusManually(value: 'date' | 'text'): void {
        if (value === 'date' && document.activeElement === this.nativeElement) {
            setTimeout(() => {
                // Firefox needs this, otherwise focus is ignored
                this.nativeElement.focus();
            }, 0);
        }
    }

    private initListeners(): void {
        const input$: Observable<string> = this.control.valueChanges;

        const firstChar$: Observable<string> = input$.pipe(
            filter(value => !!value),
            take(1)
        );

        firstChar$.pipe(takeUntil(this.destroy$)).subscribe(() => this.indeterminate$.next(false));

        input$.pipe(takeUntil(this.destroy$)).subscribe(value => {
            const dateError: DateError | null = this.getDateErrors(value);
            if (dateError) {
                this.propagateChange(new FormValue(null, FormValueState.UNSET));
                this.validationChange.emit(dateError);
            } else {
                // MED-2150 component shouldn't throw an error when it's empty and not required
                // here we check if it's empty (in case no error was thrown)
                const date: string | null = value ? DateToEngineISOString(this.getDateString(value)) : null;
                this.propagateChange(new FormValue(date, FormValueState.SET));
            }
        });
    }

    private getDateErrors(userLocaleDateString: string): DateError | null {
        const isEmpty: boolean =
            userLocaleDateString === undefined || userLocaleDateString === null || userLocaleDateString === '';
        if (isEmpty) {
            return this.required ? DateError.InvalidDate : null;
        }

        const isCorrectFormat: boolean = this.DATE_REGEXP.test(userLocaleDateString);
        if (!isCorrectFormat) {
            return DateError.InvalidFormat;
        }

        const localDate: Date = parse(userLocaleDateString, 'yyyy-MM-dd', new Date());
        if (!isValid(localDate)) {
            return DateError.InvalidDate;
        }

        if (this.min) {
            const minDate: Date = new Date(this.min);
            if (new Date(userLocaleDateString) < minDate) {
                return DateError.MinDateError;
            }
        }

        if (this.max) {
            const maxDate: Date = new Date(this.max);
            if (new Date(userLocaleDateString) > maxDate) {
                return DateError.MaxDateError;
            }
        }

        return null;
    }

    private getDateString(userLocaleDateString: string): Date {
        if (this.externalLocalTime) {
            return parse(`${userLocaleDateString} ${this.externalLocalTime}`, 'yyyy-MM-dd HH:mm:ss.SSS', new Date());
        }

        return parse(`${userLocaleDateString}`, 'yyyy-MM-dd', new Date());
    }

    private get nativeElement(): HTMLInputElement {
        return this.elementRef.nativeElement as HTMLInputElement;
    }

    protected override propagateDisabledState(state: boolean) {
        if (state && this.control.enabled) {
            this.control.disable();
        } else if (!state && this.control.disabled) {
            this.control.enable();
        }
    }
}
